<template>
  <div class="conditional-rule-row">
    <div class="conditional-rule-row__header">
      <TextInput
        :id="`rule-name-${rule.id}`"
        :modelValue="rule.name"
        @update:modelValue="$emit('update', { name: $event })"
        placeholder="Regel naam"
        size="sm"
      />
      <Btn variant="plain" size="xs" icon="trash" @click="$emit('remove')" />
    </div>

    <div class="conditional-rule-row__body">
      <!-- Condition -->
      <div class="conditional-rule-row__section">
        <Label text="Als" />
        <div class="conditional-rule-row__condition">
          <SelectInput
            :id="`rule-ctype-${rule.id}`"
            :modelValue="rule.condition.type"
            @update:modelValue="updateCondition({ type: $event })"
            :options="conditionTypeOptions"
            size="sm"
          />
          <SelectInput
            v-if="conditionNeedsOption"
            :id="`rule-copt-${rule.id}`"
            :modelValue="rule.condition.optionId"
            @update:modelValue="updateCondition({ optionId: $event })"
            :options="optionSelectOptions"
            placeholder="Kies optie"
            size="sm"
          />
          <TextInput
            v-if="conditionNeedsThreshold"
            :id="`rule-thr-${rule.id}`"
            :modelValue="String(rule.condition.threshold || '')"
            @update:modelValue="updateCondition({ threshold: Number($event) })"
            placeholder="waarde"
            size="sm"
          />
          <TextInput
            v-if="conditionNeedsChoice"
            :id="`rule-ch-${rule.id}`"
            :modelValue="rule.condition.choiceValue || ''"
            @update:modelValue="updateCondition({ choiceValue: $event })"
            placeholder="keuze waarde"
            size="sm"
          />
        </div>
      </div>

      <!-- Action -->
      <div class="conditional-rule-row__section">
        <Label text="Dan" />
        <div class="conditional-rule-row__action">
          <SelectInput
            :id="`rule-atype-${rule.id}`"
            :modelValue="rule.action.type"
            @update:modelValue="updateAction({ type: $event })"
            :options="actionTypeOptions"
            size="sm"
          />
          <TextInput
            :id="`rule-acode-${rule.id}`"
            :modelValue="rule.action.supplierCode || ''"
            @update:modelValue="updateAction({ supplierCode: $event })"
            placeholder="P95"
            size="sm"
          />
          <TextInput
            :id="`rule-aval-${rule.id}`"
            :modelValue="rule.action.value || ''"
            @update:modelValue="updateAction({ value: $event })"
            placeholder="waarde"
            size="sm"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import TextInput from '@/components/inputs/TextInput.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import Label from '@/components/partials/Label.vue';
import Btn from '@/components/partials/Btn.vue';

export default defineComponent({
  name: 'ConditionalRuleRow',
  components: { TextInput, SelectInput, Label, Btn },

  props: {
    rule: { type: Object, required: true },
    productOptions: { type: Array, default: () => [] },
  },

  emits: ['update', 'remove'],

  setup(props, { emit }) {
    const conditionTypeOptions = [
      { value: 'option_value_gt', label: 'Optie waarde >' },
      { value: 'option_value_gte', label: 'Optie waarde >=' },
      { value: 'option_value_lt', label: 'Optie waarde <' },
      { value: 'option_value_lte', label: 'Optie waarde <=' },
      { value: 'option_choice_equals', label: 'Optie keuze =' },
      { value: 'option_choice_not_equals', label: 'Optie keuze !=' },
      { value: 'option_has_value', label: 'Optie heeft waarde' },
      { value: 'option_is_empty', label: 'Optie is leeg' },
      { value: 'always', label: 'Altijd' },
    ];

    const actionTypeOptions = [
      { value: 'set_value', label: 'Waarde instellen' },
      { value: 'override_mapping', label: 'Mapping overschrijven' },
      { value: 'add_static', label: 'Statische waarde toevoegen' },
      { value: 'remove_value', label: 'Waarde verwijderen' },
      { value: 'warning', label: 'Waarschuwing tonen' },
    ];

    const optionSelectOptions = computed(() => [
      { value: null, label: '— kies optie —' },
      ...props.productOptions.map((o) => ({
        value: o.id,
        label: o.label || o.name || o.id,
      })),
    ]);

    const conditionNeedsOption = computed(() => {
      const t = props.rule.condition?.type;
      return t && t !== 'always';
    });

    const conditionNeedsThreshold = computed(() => {
      const t = props.rule.condition?.type;
      return ['option_value_gt', 'option_value_gte', 'option_value_lt', 'option_value_lte']
        .includes(t);
    });

    const conditionNeedsChoice = computed(() => {
      const t = props.rule.condition?.type;
      return ['option_choice_equals', 'option_choice_not_equals'].includes(t);
    });

    function updateCondition(partial) {
      emit('update', {
        condition: { ...props.rule.condition, ...partial },
      });
    }

    function updateAction(partial) {
      emit('update', {
        action: { ...props.rule.action, ...partial },
      });
    }

    return {
      conditionTypeOptions,
      actionTypeOptions,
      optionSelectOptions,
      conditionNeedsOption,
      conditionNeedsThreshold,
      conditionNeedsChoice,
      updateCondition,
      updateAction,
    };
  },
});
</script>

<style scoped lang="scss">
.conditional-rule-row {
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  &__condition,
  &__action {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
}
</style>
